// Is Dagilimlari — 3 modlu karsilastirma sayfasi.
//
// Layout: filtre YATAY UST panel, veri ALTTA grid. Sticky sidebar patterni
// kaldirildi; kullanici filtreyi bir kez sectikten sonra tum ekran veriye
// ayrilir (DataTable dinamigi).

import { useMemo } from 'react';
import { Box, Typography, Grid, CircularProgress, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useTranslation } from 'react-i18next';
import { PageContainer, MyButton } from '../../components';
import { useJobDistribution } from '../../hooks/useJobDistribution';
import ModeSelector from './components/ModeSelector';
import SelectorPanel from './components/SelectorPanel';
import DistributionCard from './components/DistributionCard';

const MODE_TITLE_KEYS = {
  teams:   { label: 'Ekipler Arası Dağılım' },
  members: { label: 'Ekip İçi Dağılım' },
  people:  { label: 'Kişi Bazlı Dağılım' },
};

const SectionHeader = ({ title, icon: Icon }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
      {Icon && (
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: `${theme.palette.primary.main}14`,
          color: theme.palette.primary.main,
        }}>
          <Icon sx={{ fontSize: '1rem' }} />
        </Box>
      )}
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>{title}</Typography>
      <Box sx={{
        flex: 1, height: 1,
        background: `linear-gradient(90deg, ${theme.palette.divider} 0%, transparent 100%)`,
        ml: 1,
      }} />
    </Box>
  );
};

const JobDistributionPage = () => {
  const { t } = useTranslation();
  const d = useJobDistribution();

  const hasSelection = useMemo(() => (
    (d.mode === 'teams'   && d.selectedTeamIds.length > 0) ||
    (d.mode === 'members' && !!d.selectedTeamId) ||
    (d.mode === 'people'  && d.selectedUserIds.length > 0)
  ), [d.mode, d.selectedTeamIds, d.selectedTeamId, d.selectedUserIds]);

  const pageTitle = t('navigation.jobDistributions', { defaultValue: 'İş Dağılımları' });
  const breadcrumbs = [
    { label: t('breadcrumbs.home', { defaultValue: 'Ana Sayfa' }), path: '/' },
    { label: t('navigation.jobManagement', { defaultValue: 'İş Yönetimi' }), path: '/job-management' },
  ];

  return (
    <PageContainer breadcrumbs={breadcrumbs} currentPage={pageTitle}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
        <SectionHeader
          title={d.mode ? MODE_TITLE_KEYS[d.mode].label : pageTitle}
          icon={BarChartIcon}
        />

        {/* Mode secilmemis → 3 mode karti */}
        {!d.mode && (
          <>
            {d.loadingLookups && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            )}
            <ModeSelector onSelect={d.setMode} />
          </>
        )}

        {/* Mode secili — dikey akis: geri butonu + filtre panel + veri grid */}
        {d.mode && (
          <>
            <Box sx={{ mb: 2 }}>
              <MyButton
                variant="outlined"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => { d.resetSelection(); d.setMode(null); }}
              >
                Karşılaştırma modu değiştir
              </MyButton>
            </Box>

            {/* USTTE filtre paneli — DataTable ustundeki filter bar mantigi */}
            <Box sx={{ mb: 2.5 }}>
              <SelectorPanel {...d} />
            </Box>

            {/* ALTTA veriler */}
            {!hasSelection ? (
              <Box sx={{
                py: 8, textAlign: 'center',
                border: '1px dashed', borderColor: 'divider', borderRadius: 2,
                color: 'text.disabled',
              }}>
                <Typography variant="body2">
                  Karşılaştırma için üstteki filtre panelinden seçim yap.
                </Typography>
              </Box>
            ) : d.loadingJobs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : d.distribution.length === 0 ? (
              <Box sx={{
                py: 6, textAlign: 'center', color: 'text.disabled',
                border: '1px dashed', borderColor: 'divider', borderRadius: 2,
              }}>
                <Typography variant="body2">Seçim boş</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {d.distribution.map((row) => (
                  <Grid
                    key={row.key}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: d.distribution.length > 3 ? 4 : (12 / d.distribution.length),
                      lg: d.distribution.length > 4 ? 3 : (d.distribution.length > 3 ? 4 : (12 / d.distribution.length)),
                    }}
                  >
                    <DistributionCard
                      entity={row.entity}
                      personCount={row.personCount}
                      byType={row.byType}
                      byStatus={row.byStatus}
                      total={row.total}
                      teamName={row.entity.teamName}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Box>
    </PageContainer>
  );
};

export default JobDistributionPage;
